import { useEffect, useRef } from 'react';

// Numeric 0-9 keypad with a backspace key and a 4-dot display. Used by the
// enter and set flows. Caller owns the digit string. Hardware keyboard:
// digit keys 0-9 add a digit, Backspace removes one — so a parent on a
// laptop can type the PIN instead of tapping.

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;
const PIN_LENGTH = 4;

export function PinKeypad({
  value,
  onChange,
  shake = false,
}: {
  value: string;
  onChange: (next: string) => void;
  shake?: boolean;
}) {
  const press = (digit: string) => {
    if (value.length >= PIN_LENGTH) return;
    onChange(value + digit);
  };
  const backspace = () => {
    if (value.length === 0) return;
    onChange(value.slice(0, -1));
  };

  // Bind a document-level keydown listener once on mount; refs keep it in
  // sync with the latest value/onChange without re-binding per keystroke.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        if (valueRef.current.length >= PIN_LENGTH) return;
        onChangeRef.current(valueRef.current + e.key);
        e.preventDefault();
      } else if (e.key === 'Backspace') {
        if (valueRef.current.length === 0) return;
        onChangeRef.current(valueRef.current.slice(0, -1));
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="pin-keypad-wrap">
      <div className={`pin-dots${shake ? ' shake' : ''}`} aria-label={`PIN, ${value.length}/4`}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span key={i} className={`pin-dot${i < value.length ? ' filled' : ''}`} />
        ))}
      </div>
      <div className="pin-keypad">
        {KEYS.map((k) => (
          <button key={k} type="button" className="pin-key" onClick={() => press(k)} aria-label={k}>
            {k}
          </button>
        ))}
        <span />
        <button type="button" className="pin-key" onClick={() => press('0')} aria-label="0">
          0
        </button>
        <button
          type="button"
          className="pin-key pin-key-back"
          onClick={backspace}
          aria-label="Poista"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}

export const PIN_DIGIT_COUNT = PIN_LENGTH;
